/* Auctions hub (sections/auctions.liquid) — fetches live data client-side from an external
   auction feed and renders the top-right timer/next-auction box and the lot grid below the
   hero (the hero itself is static, merchant-configured content rendered server-side by the
   .liquid file — this only fills in the one dynamic part of it). Everything here is DOM built
   from data-* attribute hooks (same convention as assets/distilleries-by-brand.js), not IDs, so
   multiple instances of the section could coexist on one page without colliding. This is the
   theme's first fetch()-based feature — there's no existing polling precedent to match beyond
   assets/dispatch-countdown.js's setInterval hygiene (cache what doesn't change per tick, clear
   intervals on teardown). */

(() => {
  const MIN_POLL_MS = 15000;
  // Total lots available to scroll through in the carousel (see .auctions__lots in
  // assets/section-auctions.css) — how many are visible at once without scrolling is a separate,
  // purely responsive CSS concern (.auctions__lot-card's flex-basis).
  const MAX_LOTS = 8;

  // Inline SVG, not a CDN/asset URL: this needs to work even if the feed (and therefore the
  // lot's own image) is unreachable, so it can't depend on a network request of its own.
  const PLACEHOLDER_IMAGE =
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">' +
      '<rect width="200" height="200" fill="#f5f1ea"/>' +
      '<path d="M85 20h30v24l14 16v104a10 10 0 0 1-10 10H81a10 10 0 0 1-10-10V60l14-16V20z" fill="none" stroke="#c9b8a0" stroke-width="3"/>' +
      '<path d="M75 92h50" stroke="#c9b8a0" stroke-width="3"/>' +
      '</svg>'
    );

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatCurrency(value, currency) {
    if (typeof value !== 'number') return null;
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        maximumFractionDigits: 0,
      }).format(value);
    } catch (error) {
      return '£' + Math.round(value).toLocaleString('en-GB');
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  // The grid is headed "Recently Sold Lots" between auctions, so it should only ever show
  // lots that actually sold. Lots that failed to meet their reserve come back from the feed
  // as status 'unsold' and are dropped here (along with any other non-sold status, e.g. a
  // stale 'upcoming'/'live' record left in featured_lots after an auction closes).
  // During a live auction the same grid becomes "Featured Lots in the Live Auction", so the
  // current lots stay — only 'unsold' is filtered out in that case.
  function isSellableLot(lot, isLive) {
    if (!lot || typeof lot !== 'object') return false;
    if (lot.status === 'unsold') return false;
    return isLive || lot.status === 'sold';
  }

  function lotBadge(lot, isLive) {
    // lot.badge is not part of today's feed response — this is a forward-compatible hook so a
    // future feed change can supply punchier per-lot copy (e.g. "Record Price", "Sold Above
    // Estimate") without a theme change. Falls back to the existing status-derived text/class
    // exactly as before when it's absent.
    const statusBadge = (() => {
      if (lot.status === 'live') {
        // Defensive: the page-level auction state says not-live (isLive false), so don't show a
        // "Live"/"Ending Soon" pill even if a stale/leftover lot record still reports
        // status "live" — that pill should only ever appear while an auction is actually live.
        if (!isLive) return { text: '', className: '' };
        const msLeft = lot.ends_at ? new Date(lot.ends_at).getTime() - Date.now() : null;
        const endingSoon = msLeft != null && msLeft > 0 && msLeft < 60 * 60 * 1000;
        return endingSoon
          ? { text: 'Ending Soon', className: 'auctions__lot-badge--ending' }
          : { text: 'Live', className: 'auctions__lot-badge--live' };
      }
      if (lot.status === 'sold') return { text: 'Sold', className: 'auctions__lot-badge--sold' };
      if (lot.status === 'unsold') return { text: 'Unsold', className: 'auctions__lot-badge--unsold' };
      return { text: '', className: '' };
    })();

    if (lot.badge && typeof lot.badge === 'string' && (lot.status !== 'live' || isLive)) {
      return { text: lot.badge, className: statusBadge.className };
    }
    return statusBadge;
  }

  // Label (small caps) on one line, then the actual price (if any) below it in the page's wine
  // accent colour — see .auctions__lot-price-label/-value in assets/section-auctions.css.
  function lotPriceHtml(lot) {
    if (lot.status === 'sold') {
      const price = formatCurrency(lot.sold_price, lot.currency);
      return price
        ? `<span class="auctions__lot-price-label">Sold Price</span><span class="auctions__lot-price-value">${escapeHtml(price)}</span>`
        : '<span class="auctions__lot-price-label">Sold</span>';
    }
    if (lot.status === 'unsold') {
      return '<span class="auctions__lot-price-label">Unsold</span>';
    }
    // status "live" (or unrecognised — treat like live rather than hide the price entirely)
    if (typeof lot.current_bid === 'number') {
      const price = formatCurrency(lot.current_bid, lot.currency);
      return `<span class="auctions__lot-price-label">Current bid</span><span class="auctions__lot-price-value">${escapeHtml(price || '')}</span>`;
    }
    const start = formatCurrency(lot.start_price, lot.currency);
    return `<span class="auctions__lot-price-label">No bids yet</span><span class="auctions__lot-price-value">${escapeHtml(start || '')}</span>`;
  }

  class AuctionsHub {
    constructor(root) {
      this.root = root;
      this.feedUrl = root.getAttribute('data-feed-url');
      this.pollMs = Math.max(parseInt(root.getAttribute('data-poll-interval'), 10) || 30000, MIN_POLL_MS);
      this.browseUrl = root.getAttribute('data-browse-url') || '#';
      // Overridable per-instance so a narrower layout (the condensed mega-menu panel) can show
      // fewer lots than the full page's 4 — defaults to MAX_LOTS unchanged for every existing
      // instance that doesn't set this.
      this.maxLots = Math.max(parseInt(root.getAttribute('data-max-lots'), 10) || MAX_LOTS, 1);

      this.timerEl = root.querySelector('[data-auctions-timer]');
      this.timerLabelEl = root.querySelector('[data-auctions-timer-label]');
      this.timerValueEl = root.querySelector('[data-auctions-timer-value]');
      this.timerSubEl = root.querySelector('[data-auctions-timer-sub]');
      this.sectionHeadingEl = root.querySelector('[data-auctions-section-heading]');
      this.sectionDescriptionEl = root.querySelector('[data-auctions-section-description]');
      this.viewAllEl = root.querySelector('[data-auctions-view-all]');
      this.ctaEl = root.querySelector('[data-auctions-cta]');
      this.lotsEl = root.querySelector('[data-auctions-lots]');
      this.lotsPrevEl = root.querySelector('[data-auctions-lots-prev]');
      this.lotsNextEl = root.querySelector('[data-auctions-lots-next]');
      this.feedMessageEl = root.querySelector('[data-auctions-feed-message]');
      this.liveOnlyNavEls = root.querySelectorAll('[data-nav-live-only]');

      this.pollTimer = null;
      this.countdownTimer = null;
      this.countdownTarget = null;
      this.hasData = false;

      this.fetchData = this.fetchData.bind(this);
      this.tickCountdown = this.tickCountdown.bind(this);
      this.updateLotsArrows = this.updateLotsArrows.bind(this);

      if (this.lotsPrevEl) this.lotsPrevEl.addEventListener('click', () => this.scrollLots(-1));
      if (this.lotsNextEl) this.lotsNextEl.addEventListener('click', () => this.scrollLots(1));
      if (this.lotsEl) this.lotsEl.addEventListener('scroll', this.updateLotsArrows, { passive: true });
      window.addEventListener('resize', this.updateLotsArrows);

      this.fetchData();
      this.pollTimer = window.setInterval(this.fetchData, this.pollMs);

      // Stop polling once the tab/page is gone rather than leaking timers indefinitely.
      window.addEventListener('pagehide', () => this.destroy(), { once: true });
    }

    // Scrolls by exactly one "page" (the row's own visible width) — matches however many cards
    // are actually showing at the current breakpoint (see .auctions__lot-card's flex-basis in
    // assets/section-auctions.css) without this needing to know that number itself.
    scrollLots(direction) {
      if (!this.lotsEl) return;
      this.lotsEl.scrollBy({ left: direction * this.lotsEl.clientWidth, behavior: 'smooth' });
    }

    // Hides (not just disables) each arrow once there's nowhere left to scroll that way — run
    // after every render (card count/widths just changed) and on scroll/resize (position or
    // visible-card-count just changed). The 1px slack absorbs sub-pixel scrollWidth/clientWidth
    // rounding so the arrows don't flicker at the exact ends.
    updateLotsArrows() {
      if (!this.lotsEl || !this.lotsPrevEl || !this.lotsNextEl) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.lotsEl;
      const maxScroll = scrollWidth - clientWidth;
      const canScroll = maxScroll > 1;
      this.lotsPrevEl.hidden = !canScroll || scrollLeft <= 1;
      this.lotsNextEl.hidden = !canScroll || scrollLeft >= maxScroll - 1;
    }

    fetchData() {
      fetch(this.feedUrl, { cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error(`Auctions feed responded with ${response.status}`);
          return response.json();
        })
        .then((data) => {
          this.hasData = true;
          this.hideFeedMessage();
          this.render(data);
        })
        .catch((error) => {
          console.error('[auctions] feed fetch failed:', error);
          // Only show the error state on a failed FIRST load. If we already have data on
          // screen from a previous successful poll, a transient failure shouldn't blank out a
          // working page — just try again on the next poll.
          if (!this.hasData) this.renderError();
        });
    }

    hideFeedMessage() {
      if (!this.feedMessageEl) return;
      this.feedMessageEl.hidden = true;
      this.feedMessageEl.classList.remove('auctions__feed-message--visible');
    }

    renderError() {
      if (this.lotsEl) this.lotsEl.innerHTML = '';
      if (this.feedMessageEl) {
        this.feedMessageEl.textContent =
          "We couldn't load live auction data right now — visit the auction site directly to browse current lots.";
        this.feedMessageEl.hidden = false;
        this.feedMessageEl.classList.add('auctions__feed-message--visible');
      }
      // Unknown state on a failed fetch — hide the timer/next-auction box (we don't know
      // current_auction/next_auction) and the live-only nav item, same safe default as the
      // initial SSR markup. The hero itself is static merchant content, so it's unaffected.
      if (this.timerEl) this.timerEl.hidden = true;
      this.stopCountdown();
      this.renderLiveOnlyNav(false);
      this.renderCta(false);
    }

    render(data) {
      const isLive = !!data.is_live;
      const lots = (Array.isArray(data.featured_lots) ? data.featured_lots : []).filter((lot) =>
        isSellableLot(lot, isLive)
      );

      this.renderTimer(data, isLive);
      this.renderSectionHeader(isLive, data);
      this.renderLots(lots, isLive);
      this.renderLiveOnlyNav(isLive);
      this.renderCta(isLive);
    }

    // Swaps the hero's "Register to Bid" button to "View Auction" (label/url both merchant-
    // configurable — see data-live-label/data-live-url, sections/auctions-hero.liquid) for as
    // long as the feed reports an auction in progress, and restores the original merchant-
    // configured label/url otherwise (auction just ended, or - via renderError - feed state is
    // unknown), same recompute-every-render approach as renderLiveOnlyNav above.
    renderCta(isLive) {
      if (!this.ctaEl) return;
      const label = isLive ? this.ctaEl.dataset.liveLabel : this.ctaEl.dataset.defaultLabel;
      const url = isLive ? this.ctaEl.dataset.liveUrl : this.ctaEl.dataset.defaultUrl;
      if (label) this.ctaEl.textContent = label;
      if (url) this.ctaEl.setAttribute('href', url);
    }

    // Toggled on every render (initial load and every poll), not just once — so a
    // "Live Auctions" link that appears when an auction starts also disappears again if it ends
    // while the page is still open.
    renderLiveOnlyNav(isLive) {
      this.liveOnlyNavEls.forEach((el) => {
        el.hidden = !isLive;
      });
    }

    renderTimer(data, isLive) {
      if (!this.timerEl) return;

      if (isLive && data.current_auction && data.current_auction.ends_at) {
        if (this.timerLabelEl) {
          const name = data.current_auction.name;
          this.timerLabelEl.textContent = name ? `${name} Auction · Lots Close In` : 'Lots Close In';
        }
        this.timerEl.hidden = false;
        this.startCountdown(data.current_auction.ends_at);
        return;
      }

      if (!isLive && data.next_auction && data.next_auction.starts_at) {
        // Handles next_auction being null: this branch is only reached when it isn't.
        this.stopCountdown();
        if (this.timerLabelEl) this.timerLabelEl.textContent = 'Next Auction';
        if (this.timerValueEl) this.timerValueEl.textContent = data.next_auction.name || '';
        if (this.timerSubEl) this.timerSubEl.textContent = formatDate(data.next_auction.starts_at);
        this.timerEl.hidden = false;
        return;
      }

      // Neither a live countdown nor a next-auction date available — hide the box instead of
      // printing "null" or an empty shell.
      this.stopCountdown();
      this.timerEl.hidden = true;
    }

    startCountdown(endsAtIso) {
      this.countdownTarget = new Date(endsAtIso).getTime();
      this.stopCountdown();
      this.tickCountdown();
      this.countdownTimer = window.setInterval(this.tickCountdown, 1000);
    }

    stopCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }

    tickCountdown() {
      if (!this.timerValueEl || this.countdownTarget == null) return;
      const msLeft = this.countdownTarget - Date.now();

      if (msLeft <= 0) {
        this.timerValueEl.textContent = 'Auction ended';
        if (this.timerSubEl) this.timerSubEl.textContent = '';
        this.stopCountdown();
        // The feed's own status has likely just flipped too — pull it rather than sit on a
        // stale "ended" countdown until the next scheduled poll (which could be up to
        // this.pollMs away).
        window.setTimeout(this.fetchData, 5000);
        return;
      }

      const totalSeconds = Math.floor(msLeft / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts = [];
      if (days > 0) {
        parts.push(`${days}d`, `${hours}h`, `${minutes}m`);
      } else if (hours > 0) {
        parts.push(`${hours}h`, `${minutes}m`, `${seconds}s`);
      } else {
        parts.push(`${minutes}m`, `${seconds}s`);
      }

      this.timerValueEl.textContent = parts.join(' ');
      if (this.timerSubEl) this.timerSubEl.textContent = 'Register to bid';
    }

    renderSectionHeader(isLive, data) {
      if (this.sectionHeadingEl) {
        this.sectionHeadingEl.textContent = isLive ? 'Featured Lots in the Live Auction' : 'Recently Sold Lots';
      }
      if (this.sectionDescriptionEl) {
        this.sectionDescriptionEl.textContent = isLive
          ? 'A selection of exceptional bottles that feature in the current auction, with full provenance and specialist appraisal.'
          : 'A selection of exceptional bottles that just sold in the latest auction, with full provenance and specialist appraisal.';
      }
      if (this.viewAllEl) {
        const url = (isLive && data.current_auction && data.current_auction.url) || this.browseUrl;
        this.viewAllEl.setAttribute('href', url);
      }
    }

    renderLots(lots, isLive) {
      if (!this.lotsEl) return;

      // Handles featured_lots being an empty array: clears any skeleton placeholders and
      // leaves the grid empty rather than rendering broken cards.
      if (lots.length === 0) {
        this.lotsEl.innerHTML = '';
        this.updateLotsArrows();
        return;
      }

      this.lotsEl.innerHTML = lots.slice(0, this.maxLots).map((lot) => this.lotCardHtml(lot, isLive)).join('');
      // New cards just changed scrollWidth (and a poll can shrink the count) — resets scroll
      // position too, since an old scrollLeft could now overshoot a shorter row.
      this.lotsEl.scrollLeft = 0;
      this.updateLotsArrows();
    }

    lotCardHtml(lot, isLive) {
      const badge = lotBadge(lot, isLive);
      const image = lot.image_url || PLACEHOLDER_IMAGE;

      return (
        `<a class="auctions__lot-card" href="${escapeHtml(lot.url || '#')}">` +
          '<span class="auctions__lot-media">' +
            `<img class="auctions__lot-image" src="${escapeHtml(image)}" alt="" loading="lazy">` +
            (badge.text
              ? `<span class="auctions__lot-badge ${badge.className}">${escapeHtml(badge.text)}</span>`
              : '') +
          '</span>' +
          '<span class="auctions__lot-body">' +
            `<span class="auctions__lot-title">${escapeHtml(lot.title || '')}</span>` +
            (lot.subtitle ? `<span class="auctions__lot-subtitle">${escapeHtml(lot.subtitle)}</span>` : '') +
            `<span class="auctions__lot-price">${lotPriceHtml(lot)}</span>` +
          '</span>' +
        '</a>'
      );
    }

    destroy() {
      if (this.pollTimer) window.clearInterval(this.pollTimer);
      this.stopCountdown();
      window.removeEventListener('resize', this.updateLotsArrows);
    }
  }

  // /pages/auctions renders sections/auctions.liquid AND the sitewide header (which can also
  // carry the condensed mega-menu panel) on the same page — both include this script tag
  // independently, so init() can genuinely run twice there. This guard makes constructing a hub
  // idempotent per-root regardless of how many times init()/initLazy() end up called, rather than
  // relying on each call site remembering not to double-fire.
  function createHubOnce(root) {
    if (root.dataset.auctionsInitialized) return;
    root.dataset.auctionsInitialized = 'true';
    new AuctionsHub(root);
  }

  // [data-auctions-hub-lazy] (the condensed mega-menu panel in snippets/mega-menu-auctions.liquid)
  // renders on every page via the header, not just /pages/auctions — constructing AuctionsHub
  // (and its immediate fetch()) unconditionally on load would mean every single pageview
  // sitewide hits the external feed even if the visitor never opens the "Auctions" dropdown.
  // Deferred instead: wait for the enclosing mega menu's <details> to actually open, and only
  // fetch then, once, the first time.
  function initLazy() {
    document.querySelectorAll('[data-auctions-hub-lazy]').forEach((root) => {
      const details = root.closest('details');
      if (!details) {
        // No mega menu wrapper found (unexpected markup) — fall back to the safe default rather
        // than never initialising at all.
        createHubOnce(root);
        return;
      }
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        createHubOnce(root);
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-auctions-hub]').forEach((root) => createHubOnce(root));
    initLazy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
